import { Box, Title, Button, sanitizeToNumeric } from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FormikErrors, FormikTouched } from 'formik';
import useFetchWorkflows from '@/hooks/useApi/useFetchWorkflows';
import useFetchTransactionTypes from '@/hooks/useApi/useFetchTransactionTypes';
import UpdateWorkFlowForm, { UpdateWorkflow, UpdateWorkflowContainer } from './UpdateWorkFlowForm';
import { useApprovalWorkflow } from '@/providers/ApprovalWorkflow.provider';
import usePostUpdateWorkflow from '@/hooks/useApi/usePostUpdateWorkflow';
import usePostCreateWorkflow from '@/hooks/useApi/usePostCreateWorkflow';
import { Trans, useTranslation } from 'react-i18next';
import { useClientData } from '@/providers/ClientData.provider';
import { UtilButton } from 'UI';
import { useDialog } from '@/providers/Dialog.provider';
import WorkflowCreatedDialog from './WorkflowCreatedDialog';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useToast } from '@/providers/Toast.provider';

type FC = FunctionComponent;

const UpdateWorkFlowContainer: FC = () => {
  const history = useHistory();
  const dialog = useDialog();
  const { id } = useParams<{ id: string }>();
  const isCreate = !id;
  const [isView, setIsView] = useState(!isCreate);
  const [isReview, setIsReview] = useState(false);
  const { workflows, fetchWorkflows } = useFetchWorkflows();
  const [transactionTypesResponse, fetchTransactionTypes] = useFetchTransactionTypes({});

  // first element is undefined to set the first form as empty
  const [workflowsList, setWorkFlowList] = useState<(UpdateWorkflowContainer | undefined)[]>([undefined]);

  const [, , updateWorkflowAsync] = usePostUpdateWorkflow({});
  const [, , createWorkFlowAsync] = usePostCreateWorkflow({});
  const { approvalListReset, transactionsReset, getPaymentRail } = useApprovalWorkflow();
  const [hasAddNew, setHasNew] = useState(true);
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { t } = useTranslation('translations', { keyPrefix: 'TransactionApproval.Configuration.Workflows.Update' });
  const alertDialog = useAlertDialog();
  const toast = useToast();

  const {
    response: { Country },
  } = useClientData();

  useEffect(() => {
    if (workflowsList) {
      const workflowsAdded = workflowsList.flatMap((workflow) => workflow?.values?.TransactionTypes || []);

      const existingWorkflows = workflows.flatMap((workflow) =>
        getPaymentRail(workflow.VIOAccountTransactionTypes_ID || '0', transactionTypesResponse),
      );

      const workflowAll = [...workflowsAdded, ...existingWorkflows];

      const filteredWorkflow = Object.values(transactionTypesResponse?.TransactionTypes || {}).filter(
        (transactionType) => {
          const exists = workflowAll.some((workflow) => workflow === transactionType.TransactionType);
          return !exists;
        },
      );
      setHasNew(filteredWorkflow.length > 0);
    }
  }, [workflowsList, workflows, transactionTypesResponse]);

  const resetWorkflowList = () => {
    const newWorkflow = workflows
      .map((workflow) => ({
        values: {
          ...workflow,
          TransactionTypes: getPaymentRail(workflow.VIOAccountTransactionTypes_ID || '0', transactionTypesResponse),
        },
      }))
      .filter((workflow) => workflow.values.ID.toString() === id);

    setWorkFlowList(newWorkflow);
  };

  useEffect(() => {
    if (id) {
      resetWorkflowList();
    }
  }, [workflows, id, transactionTypesResponse]);

  useEffect(() => {
    refreshWorkflows();

    fetchTransactionTypes({
      CountriesID: Country === 'CA' ? 1 : 2,
      IsTransactionApprovalSupported: 1,
    });
  }, []);

  async function refreshWorkflows() {
    try {
      await fetchWorkflows();
    } catch (err) {
      alertDialog.error(tGlobal('error'), tGlobal('errorOccurred'));
    }
  }
  const updateWorkflows = async () => {
    const updatedWorkflowsList = [...workflowsList];
    try {
      for (let i = updatedWorkflowsList.length - 1; i >= 0; i--) {
        const workflow = updatedWorkflowsList[i];
        if (workflow) {
          const res = await updateWorkflowAsync({
            WorkflowID: workflow?.values.ID || '0',
            NumberApprovalsRequired: workflow?.values.NumberApprovalsRequired || 0,
            MinAmount: Number(sanitizeToNumeric(String(workflow?.values.MinAmount || 0))),
          });

          if (!res.Success) {
            throw Error(res.ErrorMessage);
          }

          // Remove the workflow after successful update
          updatedWorkflowsList.splice(i, 1);
        }
      }
      transactionsReset();
      approvalListReset();
      history.push({
        pathname: '/transaction-approval/configuration/workflows',
        state: {
          isCreate: true,
          hasCreateMultiple: workflowsList.length > 1,
          isUpdate: !isCreate,
          numberCreated: workflowsList.length,
          paymentRailName: !isCreate
            ? getPaymentRail(workflowsList[0]?.values.VIOAccountTransactionTypes_ID || '0', transactionTypesResponse)
            : '',
        },
      });
    } catch (err) {
      // Update the workflow list with remaining workflows that weren't updated
      setWorkFlowList(updatedWorkflowsList);
      fetchWorkflows();

      alertDialog.error(tGlobal('error'), err instanceof Error ? err.message : tGlobal('errorOccurred'));
    }
  };
  const createWorkflows = async () => {
    const updatedWorkflowsList = [...workflowsList];
    try {
      let numberCreated = 0;

      for (let i = updatedWorkflowsList.length - 1; i >= 0; i--) {
        const workflow = updatedWorkflowsList[i];
        if (workflow && workflow.values.TransactionTypes) {
          const types = [...workflow.values.TransactionTypes];

          for (let j = types.length - 1; j >= 0; j--) {
            const transactionType = types[j];
            const res = await createWorkFlowAsync({
              TransactionType: transactionType || '',
              NumberApprovalsRequired: workflow?.values.NumberApprovalsRequired || 0,
              MinAmount: workflow?.values.MinAmount || 0,
            });

            if (!res.Success) {
              throw Error(res.ErrorMessage);
            }
            types.splice(j, 1);
            //updates workflow in updatedWorkflowsList because it is a refrence to a clone
            workflow.values.TransactionTypes = types;

            numberCreated += 1;
            // Remove the successfully created transaction type
          }

          // Remove the workflow after all its types are created successfully
          updatedWorkflowsList.splice(i, 1);
        }
      }

      toast.success(
        <Trans
          i18nKey="TransactionApproval.Configuration.Workflows.Update.successfullyCreatedToast"
          ns="translations"
          defaultValue="<0>Workflow Create</0> The workflow has been created successfully."
          components={[<span style={{ fontWeight: 700 }} />]}
        />,
      );
      const resp = await dialog.open<boolean>(({ onConfirm, onCancel }) => (
        <WorkflowCreatedDialog onConfirm={onConfirm} onCancel={onCancel} />
      ));

      transactionsReset();
      approvalListReset();
      if (resp) {
        history.push('/transaction-approval/configuration/roles');
      } else {
        history.push('/transaction-approval/configuration/workflows');
      }
    } catch (err) {
      // Update the workflow list with remaining workflows that weren't created
      setWorkFlowList(updatedWorkflowsList);
      fetchWorkflows();

      alertDialog.error(tGlobal('error'), err instanceof Error ? err.message : tGlobal('errorOccurred'));
    }
  };

  return (
    <div>
      <Box flx="col" mb="md" gapSize="16px">
        <UtilButton
          iconLeft="chevron_left"
          size="large"
          onClick={() => {
            if (history.length > 1) {
              history.goBack();
            } else {
              history.replace('/transaction-approval/configuration/workflows');
            }
          }}
        >
          {history.length > 1 ? tGlobal('Buttons.back') : t('backButton')}
        </UtilButton>

        <Title title={isCreate ? t('createTitle') : isView ? t('viewTitle') : t('editTitle')} />
      </Box>
      <Box mt="md" mb="lg" flx="col" gap="lg" width="100%">
        {workflowsList.map((workflow, index) => (
          <Box width="100%">
            <UpdateWorkFlowForm
              id={index}
              defaultValues={workflow?.values}
              isView={isView}
              isReview={isReview}
              onChange={(
                FormId: number,
                values: UpdateWorkflow,
                errors: FormikErrors<UpdateWorkflow>,
                touched: FormikTouched<UpdateWorkflow>,
                submitForm: () => Promise<void>,
              ) => {
                const workflowListNew = [...workflowsList];
                workflowListNew[FormId] = {
                  values: { ...values },
                  errors,
                  touched,
                  submitForm,
                };
                setWorkFlowList(workflowListNew);
              }}
              workflowList={workflowsList}
              onDelete={(idDelete: number) => {
                setWorkFlowList(workflowsList.filter((_, i) => i !== idDelete));
              }}
              isDelete={index > 0}
              isCreate={isCreate}
              workflows={workflows}
              transactionTypesResponse={transactionTypesResponse}
            />
          </Box>
        ))}
      </Box>
      {isCreate && hasAddNew && !isReview && (
        <Box mt="md" mb="md" jc="fs">
          <Button
            id="add-new-workflow"
            label={t('Buttons.addNewWorkflow')}
            color="secondary"
            icon="add"
            iconLocation="left"
            iconHeight="12px"
            iconWidth="12px"
            onClick={() => {
              setWorkFlowList([...workflowsList, undefined]);
            }}
          />
        </Box>
      )}
      <Box jc="fe">
        <Box gap="sm">
          {!isView && (
            <>
              {!isCreate && !isReview && (
                <Button
                  id="approval-cancel-workflow"
                  label={t('Buttons.cancel')}
                  color="secondary"
                  onClick={() => {
                    resetWorkflowList();
                    setIsView(true);
                  }}
                />
              )}
              {isReview && (
                <Button
                  id="approval-cancel-review"
                  label={t('Buttons.cancel')}
                  color="secondary"
                  onClick={() => {
                    setIsReview(false);
                  }}
                />
              )}
              <Button
                id="approval-update-roles"
                label={
                  isReview
                    ? isCreate
                      ? t('Buttons.createWorkflow')
                      : t('Buttons.save')
                    : t('Buttons.reviewWorkflow', { defaultValue: 'Review Workflow' })
                }
                isDisabled={workflowsList.some(
                  (workflow) => workflow?.errors && Object.keys(workflow.errors).length > 0,
                )}
                color="primary"
                onClick={() => {
                  const numberOfErrors = workflowsList.reduce((accumulator, currentValue) => {
                    if (currentValue && currentValue.errors) {
                      return accumulator + Object.keys(currentValue.errors).length;
                    }
                    return accumulator;
                  }, 0);

                  if (numberOfErrors > 0) {
                    workflowsList.forEach((workflow) => {
                      if (workflow && workflow.submitForm) {
                        workflow.submitForm();
                      }
                    });
                  } else if (!isReview) {
                    // Enter review mode
                    setIsReview(true);
                  } else if (isCreate) {
                    createWorkflows();
                  } else {
                    updateWorkflows();
                  }
                }}
              />
            </>
          )}
          {isView && (
            <Button
              id="approval-edit-workflow"
              label={t('Buttons.editWorkflow')}
              color="primary"
              onClick={() => {
                setIsView(false);
              }}
            />
          )}
        </Box>
      </Box>
    </div>
  );
};
export default UpdateWorkFlowContainer;
